/**
 * Protected Route component for trAInedup.ai
 * Implements role-based access control for routes
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import { ROUTES } from '../../utils/constants';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredRole?: UserRole;
  requiredRoles?: UserRole[]; // New prop for multiple roles
  requiredSubscription?: boolean;
  redirectTo?: string;
}

/**
 * Protected route component that handles authentication and authorization
 * Redirects users based on their authentication status and role
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requiredRole,
  requiredRoles,
  requiredSubscription = false,
  redirectTo,
}) => {
  const { user, loading, isAuthenticated, hasRole, isSubscriptionActive } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    return (
      <Navigate
        to={redirectTo || ROUTES.login}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check email verification
  if (requireAuth && isAuthenticated && user && !user.emailVerified) {
    return <Navigate to={ROUTES.emailVerificationSent} replace />;
  }

  // Check role requirement (single role)
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <Navigate
        to={ROUTES.unauthorized}
        state={{ 
          from: location,
          requiredRole,
          userRoles: user?.roles 
        }}
        replace
      />
    );
  }

  // Check role requirement (multiple roles)
  if (requiredRoles && !requiredRoles.some(role => hasRole(role))) {
    return (
      <Navigate
        to={ROUTES.unauthorized}
        state={{ 
          from: location,
          requiredRoles,
          userRoles: user?.roles 
        }}
        replace
      />
    );
  }

  // Check subscription requirement
  if (requiredSubscription && !isSubscriptionActive()) {
    return (
      <Navigate
        to={ROUTES.pricing}
        state={{ 
          from: location,
          message: 'This feature requires an active subscription.' 
        }}
        replace
      />
    );
  }

  // If all checks pass, render the protected content
  return <>{children}</>;
};

/**
 * Higher-order component for protecting routes with authentication
 */
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<ProtectedRouteProps, 'children'>
) => {
  return (props: P) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

/**
 * Higher-order component for protecting routes with role requirements
 */
export const withRole = <P extends object>(
  Component: React.ComponentType<P>,
  requiredRole: UserRole,
  options?: Omit<ProtectedRouteProps, 'children' | 'requiredRole'>
) => {
  return (props: P) => (
    <ProtectedRoute requiredRole={requiredRole} {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

/**
 * Higher-order component for protecting routes with subscription requirements
 */
export const withSubscription = <P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<ProtectedRouteProps, 'children' | 'requiredSubscription'>
) => {
  return (props: P) => (
    <ProtectedRoute requiredSubscription={true} {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

